import { useForm } from '@conform-to/react'
import { parse } from '@conform-to/zod'
import { json, type DataFunctionArgs } from '@remix-run/node'
import {
	Form,
	useActionData,
	useLoaderData,
	type MetaFunction,
} from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { DocsPageHeader } from '#app/components/page-header.tsx'
import { TableOfContents } from '#app/components/pageLayout/TableOfContents.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { getContentNoteBySlug } from '#app/utils/content-notes.server.ts'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { invariantResponse, useIsPending } from '#app/utils/misc.tsx'
import { requireUserWithPermission } from '#app/utils/permissions.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { type loader as notesLoader } from './posts.tsx'

export async function loader({ params }: DataFunctionArgs) {
	const dbNote = await prisma.note.findFirst({
		where: { slug: params.noteId },
		select: {
			id: true,
			title: true,
			content: true,
			ownerId: true,
			updatedAt: true,
			images: {
				select: {
					id: true,
					altText: true,
				},
			},
		},
	})

	const fallbackNote =
		dbNote ??
		(() => {
			const contentNote = getContentNoteBySlug(params.noteId ?? '')
			if (!contentNote) return null
			return {
				id: contentNote.slug,
				title: contentNote.title,
				content: contentNote.content,
				ownerId: 'content-fallback',
				updatedAt: new Date(),
				images: [],
			}
		})()

	invariantResponse(fallbackNote, 'Not found', { status: 404 })

	const date = new Date(fallbackNote.updatedAt)
	const timeAgo = formatDistanceToNow(date)

	return json({
		note: fallbackNote,
		timeAgo,
	})
}

const DeleteFormSchema = z.object({
	intent: z.literal('delete-note'),
	noteId: z.string(),
})

export async function action({ request }: DataFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()
	await validateCSRF(formData, request.headers)
	const submission = parse(formData, {
		schema: DeleteFormSchema,
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	const { noteId } = submission.value

	const note = await prisma.note.findFirst({
		select: { id: true, ownerId: true, owner: { select: { username: true } } },
		where: { id: noteId },
	})
	invariantResponse(note, 'Not found', { status: 404 })

	const isOwner = note.ownerId === userId
	await requireUserWithPermission(
		request,
		isOwner ? `delete:note:own` : `delete:note:any`,
	)

	await prisma.note.delete({ where: { id: note.id } })

	return redirectWithToast(`/users/${note.owner.username}/notes`, {
		type: 'success',
		title: 'Success',
		description: 'Your note has been deleted.',
	})
}

interface Heading {
	id: string
	text: string | null
	heading: string | null
}
export default function NoteRoute() {
	const data = useLoaderData<typeof loader>()
	/* const user = useOptionalUser()
	const isOwner = user?.id === data.note.ownerId
	const canDelete = userHasPermission(
		user,
		isOwner ? `delete:note:own` : `delete:note:any`,
	)
	const displayBar = canDelete || isOwner */

	// Replace headings with modified versions containing IDs
	function generateTOCAndHTML(htmlString) {
		const headingRegex = /<(h[2-3])>(.+?)<\/\1>/gi
		const toc: Heading[] = []
		const modifiedHtml = htmlString.replace(
			headingRegex,
			(match, headingLevel, headingText) => {
				const id = headingText.trim().toLowerCase().replace(/\s+/g, '-')
				toc.push({ text: headingText, id, heading: headingLevel })
				return `<${headingLevel} id="${id}">${headingText}</${headingLevel}>`
			},
		)

		return {
			toc: toc,
			modifiedHtml,
		}
	}

	const { toc, modifiedHtml } = generateTOCAndHTML(data.note.content)
	return (
		<>
			<main className="relative py-6 lg:gap-10 lg:py-10 xl:grid xl:grid-cols-[1fr_300px]">
				<div className="mx-auto w-full min-w-0">
					<DocsPageHeader heading={data.note.title} text={''} />
					<div
						dangerouslySetInnerHTML={{ __html: modifiedHtml }}
						className="prose prose-a:text-fuchsia-500 prose-a:no-underline hover:prose-a:font-bold hover:prose-a:text-fuchsia-700 "
					/>
					{/* <hr className="my-4 md:my-6" /> */}
					{/*  <DocsPager doc={doc} /> */}
				</div>

				<div className="hidden text-sm lg:block">
					<div className="sticky top-16 -mt-10 max-h-[calc(var(--vh)-4rem)] overflow-y-auto pt-10">
						<TableOfContents headings={toc} />
					</div>
				</div>
			</main>
		</>
	)
}

export function DeleteNote({ id }: { id: string }) {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [form] = useForm({
		id: 'delete-note',
		lastSubmission: actionData?.submission,
	})

	return (
		<Form method="POST" {...form.props}>
			<AuthenticityTokenInput />
			<input type="hidden" name="noteId" value={id} />
			<StatusButton
				type="submit"
				name="intent"
				value="delete-note"
				variant="destructive"
				status={isPending ? 'pending' : actionData?.status ?? 'idle'}
				disabled={isPending}
				className="w-full max-md:aspect-square max-md:px-0"
			>
				<Icon name="trash" className="scale-125 max-md:scale-150">
					<span className="max-md:hidden">Delete</span>
				</Icon>
			</StatusButton>
			<ErrorList errors={form.errors} id={form.errorId} />
		</Form>
	)
}

export const meta: MetaFunction<
	typeof loader,
	{ 'routes/posts/$noteId': typeof notesLoader }
> = ({ data, params, matches }) => {
	const noteTitle = data?.note.title ?? 'Post'
	const noteContentsSummary =
		data && data.note.content.length > 100
			? data?.note.content.slice(0, 97) + '...'
			: 'No hay contenido'
	return [
		{ title: noteTitle },
		{
			name: 'description',
			content: noteContentsSummary,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>You are not allowed to do that</p>,
				404: ({ params }) => (
					<p>No note with the id "{params.noteId}" exists</p>
				),
			}}
		/>
	)
}
